import React from 'react';
import { 
  Check, 
  Clock, 
  Heart, 
  Shield, 
  Users, 
  DollarSign,
  Star,
  Award
} from 'lucide-react';
import { Banner } from './Banner';


export const Pricing = () => {
  const [showPartnerPricing, setShowPartnerPricing] = React.useState(false);

  // Payment handler for future integration
  const handlePackagePurchase = (packageData) => {
    // This will be connected to Stripe/PayPal when you set up payment processing
    console.log('Package selected:', packageData);
    
    // For now, show an alert with package details
    alert(`Selected: ${packageData.title}\nPrice: $${packageData.discountedPrice}\nSavings: $${packageData.savings}\n\nPayment processing will be available once Stripe/PayPal is configured.`);
    
    // Future implementation will redirect to payment processor
    // Example: window.location.href = `/checkout?package=${packageData.title}&amount=${packageData.discountedPrice}`;
  };

  // Add click handlers to payment buttons
  React.useEffect(() => {
    const paymentButtons = document.querySelectorAll('.payment-button');
    paymentButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const packageData = JSON.parse(e.target.getAttribute('data-package'));
        handlePackagePurchase(packageData);
      });
    });

    // Cleanup event listeners
    return () => {
      paymentButtons.forEach(button => {
        button.removeEventListener('click', handlePackagePurchase);
      });
    };
  }, []);
  const benefits = [
    {
      icon: Clock,
      title: "Extended Appointment Times",
      description: "Full hour sessions dedicated to your recovery, not rushed 15-minute insurance appointments."
    },
    {
      icon: Heart,
      title: "Personalized Treatment Plans", 
      description: "Treatment designed specifically for your needs and goals, not limited by insurance coverage."
    },
    {
      icon: Shield,
      title: "No Insurance Restrictions",
      description: "Access to all treatment techniques without waiting for pre-approvals or coverage limitations."
    },
    {
      icon: Users,
      title: "One-on-One Care",
      description: "Individual attention throughout your entire session - no sharing time with multiple patients."
    },
    {
      icon: Star,
      title: "Direct Access",
      description: "Immediate scheduling and communication with Douglas Mitchell, DPT without insurance gatekeepers."
    },
    {
      icon: Award,
      title: "Results-Focused",
      description: "Treatment decisions based on what gets you better faster, not what insurance will reimburse."
    }
  ];

  const pricingPlans = [
    {
      title: "Initial Consultation & Evaluation",
      price: "$150",
      description: "Comprehensive assessment and treatment plan development",
      features: [
        "Detailed medical history review",
        "Comprehensive physical examination",
        "Movement and functional assessment", 
        "Personalized treatment plan creation",
        "Goal setting and timeline discussion",
        "Initial treatment session included"
      ],
      highlight: false
    },
    {
      title: "Follow-Up Treatment Sessions",
      price: "$125",
      description: "One-hour individualized treatment sessions",
      features: [
        "Full 60-minute treatment session",
        "One-on-one therapist attention",
        "Manual therapy techniques",
        "Therapeutic exercises",
        "Progress monitoring",
        "Home exercise program updates"
      ],
      highlight: true
    }
  ];

  const discounts = [
    {
      title: "Alexander Medical Center Patients",
      discount: "$25 off each visit",
      description: "Current patients of our collaboration partner receive special pricing"
    },
    {
      title: "Chandler Health & Performance Members", 
      discount: "$25 off each visit",
      description: "Active members receive discounted rates on all services"
    }
  ];

  const packageDeals = [
    {
      title: "4-Visit Package",
      sessions: 4,
      regularPrice: 500, // 4 x $125 = $500
      // General pricing: Just apply percentage discount to regular price
      discountedPrice: 450, // $500 - ($500 x 0.10) = $450
      // Partner pricing: Apply $25/visit first, then percentage discount
      // Step 1: $500 - ($25 x 4) = $400
      // Step 2: $400 - ($400 x 0.10) = $360
      partnerPrice: 360,
      discount: "10% OFF",
      partnerDiscount: "10% OFF + $25/visit",
      savings: 50, // $500 - $450 = $50
      partnerSavings: 140, // $500 - $360 = $140
      description: "Perfect for short-term rehabilitation goals",
      features: [
        "4 one-hour treatment sessions",
        "Personalized treatment plan",
        "Progress tracking and adjustments",
        "Home exercise program",
        "Valid for 3 months"
      ],
      popular: false,
      color: "border-gray-700"
    },
    {
      title: "8-Visit Package",
      sessions: 8,
      regularPrice: 1000, // 8 x $125 = $1000
      // General pricing: Just apply percentage discount to regular price
      discountedPrice: 880, // $1000 - ($1000 x 0.12) = $880
      // Partner pricing: Apply $25/visit first, then percentage discount
      // Step 1: $1000 - ($25 x 8) = $800
      // Step 2: $800 - ($800 x 0.12) = $704
      partnerPrice: 704,
      discount: "12% OFF",
      partnerDiscount: "12% OFF + $25/visit",
      savings: 120, // $1000 - $880 = $120
      partnerSavings: 296, // $1000 - $704 = $296
      description: "Ideal for comprehensive rehabilitation programs",
      features: [
        "8 one-hour treatment sessions",
        "Complete treatment program",
        "Bi-weekly progress assessments",
        "Advanced manual therapy techniques",
        "Valid for 4 months"
      ],
      popular: true,
      color: "border-yellow-500"
    },
    {
      title: "12-Visit Package",
      sessions: 12,
      regularPrice: 1500, // 12 x $125 = $1500
      // General pricing: Just apply percentage discount to regular price
      discountedPrice: 1275, // $1500 - ($1500 x 0.15) = $1275
      // Partner pricing: Apply $25/visit first, then percentage discount
      // Step 1: $1500 - ($25 x 12) = $1200
      // Step 2: $1200 - ($1200 x 0.15) = $1020
      partnerPrice: 1020,
      discount: "15% OFF",
      partnerDiscount: "15% OFF + $25/visit",
      savings: 225, // $1500 - $1275 = $225
      partnerSavings: 480, // $1500 - $1020 = $480
      description: "Best value for long-term treatment plans",
      features: [
        "12 one-hour treatment sessions",
        "Extended rehabilitation program",
        "Weekly progress evaluations",
        "All manual therapy techniques included",
        "Priority scheduling",
        "Valid for 6 months"
      ],
      popular: false,
      color: "border-green-500"
    }
  ];

  return (
    <div className="page-content">
      {/* Shared Banner + Pill (reduced) with top spacing */}
      <Banner pillText="Transparent Pricing" title="Cash-Based Physical Therapy" reduced={true} />

      {/* Hero Section */}
      <section className="pt-8 pb-20 bg-gradient-to-br from-black to-gray-900">
        <div className="container">
          {/* Removed duplicate title/subtitle to avoid repetition with Banner */}
          <div className="text-center max-w-4xl mx-auto"></div>
        </div>
      </section>

      {/* Why Cash-Based */}
      <section className="pad-120">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-6">
              Why Choose Cash-Based Physical Therapy?
            </h2>
            <p className="body-large max-w-3xl mx-auto">
              Insurance-based physical therapy often limits treatment options and session times. 
              Our cash-based approach puts your recovery first.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="benefit-card p-6 rounded-xl border transition-colors hover:border-opacity-80" style={{ borderColor: 'var(--border-brick-subtle)' }}>
                  <div className="flex items-center justify-center w-14 h-14 rounded-xl mb-4" style={{ backgroundColor: 'rgba(232, 184, 109, 0.15)' }}>
                    <IconComponent style={{ color: 'var(--brand-primary)' }} size={24} />
                  </div>
                  <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>
                    {benefit.title}
                  </h3>
                  <p className="body-medium text-gray-400">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="pad-120 bg-gray-900">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-6">
              Simple, Transparent Pricing
            </h2>
            <p className="body-large max-w-3xl mx-auto">
              No hidden fees, no surprise charges. Just honest, upfront pricing 
              for quality physical therapy care.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index} 
                className={`pricing-plan relative p-8 rounded-xl border transition-all hover:scale-105 ${
                  plan.highlight 
                    ? 'border-yellow-500 bg-gradient-to-br from-gray-800 to-gray-900' 
                    : 'border-gray-800 bg-gray-800'
                }`}
              >
                {plan.highlight && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="px-4 py-2 text-sm font-semibold rounded-full" style={{ backgroundColor: 'var(--brand-primary)', color: 'black' }}>
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="heading-3 mb-4" style={{ color: 'var(--brand-primary)' }}>
                    {plan.title}
                  </h3>
                  <div className="text-5xl font-bold mb-4" style={{ color: 'var(--brand-primary)' }}>
                    {plan.price}
                  </div>
                  <p className="body-medium text-gray-400">
                    {plan.description}
                  </p>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <Check style={{ color: 'var(--brand-primary)' }} size={20} className="mt-1 mr-3 flex-shrink-0" />
                      <span className="body-medium">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button 
                  className="w-full btn-primary text-lg py-3"
                  onClick={() => {
                    window.location.href = '/contact?focus=contact';
                  }}
                >
                  Schedule Appointment
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Package Deals */}
      <section className="pad-120">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-6">
              Treatment Package Deals
            </h2>
            <p className="body-large max-w-3xl mx-auto mb-8">
              Save money and commit to your recovery with our prepaid treatment packages. 
              Perfect for comprehensive rehabilitation programs.
            </p>
            
            {/* Pricing Toggle */}
            <div className="flex items-center justify-center mb-8">
              <div className="bg-gray-900 rounded-xl p-2 border" style={{ borderColor: 'var(--border-brick-subtle)' }}>
                <div className="flex items-center space-x-4">
                  <button
                    className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                      !showPartnerPricing 
                        ? 'bg-yellow-500 text-black' 
                        : 'text-gray-300 hover:text-white'
                    }`}
                    onClick={() => setShowPartnerPricing(false)}
                  >
                    General Pricing
                  </button>
                  <button
                    className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                      showPartnerPricing 
                        ? 'bg-yellow-500 text-black' 
                        : 'text-gray-300 hover:text-white'
                    }`}
                    onClick={() => setShowPartnerPricing(true)}
                  >
                    Partner Facility Pricing
                  </button>
                </div>
              </div>
            </div>

            {/* Partner Pricing Note */}
            {showPartnerPricing && (
              <div className="bg-blue-900/20 border border-blue-700/50 rounded-xl p-6 max-w-3xl mx-auto mb-8">
                <h3 className="heading-4 mb-3" style={{ color: '#60a5fa' }}>Partner Facility Discount Applied!</h3>
                <p className="body-medium text-blue-200 mb-2">
                  These prices include the additional $25 per visit discount for:
                </p>
                <div className="flex flex-wrap justify-center gap-4 text-sm mb-3">
                  <span className="bg-blue-800/50 px-3 py-1 rounded-full">✓ Alexander Medical Center Patients</span>
                  <span className="bg-blue-800/50 px-3 py-1 rounded-full">✓ Chandler Health & Performance Members</span>
                </div>
                <div className="text-xs text-blue-300">
                  <strong>Calculation:</strong> Regular price → Apply $25/visit discount → Apply package percentage discount
                </div>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packageDeals.map((pkg, index) => {
              const currentPrice = showPartnerPricing ? pkg.partnerPrice : pkg.discountedPrice;
              const currentDiscount = showPartnerPricing ? pkg.partnerDiscount : pkg.discount;
              const currentSavings = showPartnerPricing ? pkg.partnerSavings : pkg.savings;
              
              return (
                <div 
                  key={index} 
                  className={`package-deal relative p-8 rounded-xl border-2 transition-all hover:scale-105 bg-gray-900 ${pkg.color}`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="px-4 py-2 text-sm font-semibold rounded-full bg-yellow-500 text-black">
                        Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="absolute -top-3 -right-3">
                    <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold transform rotate-12">
                      {currentDiscount}
                    </div>
                  </div>

                  <div className="text-center mb-6">
                    <h3 className="heading-3 mb-2" style={{ color: 'var(--brand-primary)' }}>
                      {pkg.title}
                    </h3>
                    <p className="body-medium text-gray-400 mb-4">
                      {pkg.description}
                    </p>
                    
                    <div className="pricing-display mb-4">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <span className="text-2xl text-gray-500 line-through">
                          ${pkg.regularPrice}
                        </span>
                        <span className="text-4xl font-bold" style={{ color: 'var(--brand-primary)' }}>
                          ${currentPrice}
                        </span>
                      </div>
                      <div className="text-green-400 font-semibold">
                        You Save ${currentSavings}!
                      </div>
                      <div className="text-sm text-gray-400 mt-1">
                        ${Math.round(currentPrice / pkg.sessions)} per session
                      </div>
                      {showPartnerPricing && (
                        <div className="text-xs text-blue-300 mt-2 bg-blue-900/30 px-3 py-1 rounded-full inline-block">
                          Partner facility discount applied
                        </div>
                      )}
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <Check style={{ color: 'var(--brand-primary)' }} size={18} className="mt-1 mr-3 flex-shrink-0" />
                        <span className="body-small text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="space-y-3">
                    <button 
                      className="w-full btn-primary text-lg py-3 payment-button" 
                      data-package={JSON.stringify({
                        ...pkg,
                        discountedPrice: currentPrice,
                        savings: currentSavings,
                        discount: currentDiscount,
                        isPartnerPricing: showPartnerPricing
                      })}
                    >
                      Purchase Package
                    </button>
                    <p className="text-xs text-gray-500 text-center">
                      Secure payment • Valid for {pkg.sessions <= 4 ? '3' : pkg.sessions <= 8 ? '4' : '6'} months
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <div className="bg-gray-900 rounded-xl p-6 border max-w-3xl mx-auto" style={{ borderColor: 'var(--border-brick-subtle)' }}>
              <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>
                Package Benefits
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-300">
                <div className="flex items-center justify-center">
                  <DollarSign style={{ color: 'var(--brand-primary)' }} size={20} className="mr-2" />
                  <span>Significant Savings</span>
                </div>
                <div className="flex items-center justify-center">
                  <Star style={{ color: 'var(--brand-primary)' }} size={20} className="mr-2" />
                  <span>Priority Scheduling</span>
                </div>
                <div className="flex items-center justify-center">
                  <Shield style={{ color: 'var(--brand-primary)' }} size={20} className="mr-2" />
                  <span>Extended Validity</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Discounts */}
      <section className="pad-120 bg-gray-900">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-6">
              Additional Discounts Available
            </h2>
            <p className="body-large max-w-3xl mx-auto">
              Partner facility patients and members receive additional savings on both 
              individual sessions AND package deals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {discounts.map((discount, index) => (
              <div key={index} className="discount-card p-8 rounded-xl border bg-gray-900 text-center" style={{ borderColor: 'var(--border-brick-subtle)' }}>
                <DollarSign style={{ color: 'var(--brand-primary)' }} size={48} className="mx-auto mb-4" />
                <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>
                  {discount.title}
                </h3>
                <div className="text-3xl font-bold mb-4" style={{ color: 'var(--brand-primary)' }}>
                  {discount.discount}
                </div>
                <p className="body-medium text-gray-400">
                  {discount.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="bg-gray-900 rounded-xl p-8 border max-w-3xl mx-auto" style={{ borderColor: 'var(--border-brick-subtle)' }}>
              <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>
                Payment Methods Accepted
              </h3>
              <p className="body-medium text-gray-400 mb-4">
                We accept cash, credit cards, and HSA/FSA payments for your convenience.
              </p>
              <p className="body-small text-gray-500">
                Package discounts and prepayment options coming soon - contact us for details.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="pad-120 bg-gradient-to-r from-gray-900 to-black">
        <div className="container">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="heading-2 mb-6">
              Ready to Experience Personalized Physical Therapy?
            </h2>
            <p className="body-large mb-8">
              Schedule your initial consultation with Douglas Mitchell, DPT and discover 
              the difference that individualized, insurance-free care can make.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                className="btn-primary text-lg px-8 py-4"
                onClick={() => {
                  window.location.href = '/contact?focus=contact';
                }}
              >
                Schedule Initial Consultation
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};